import React, { useState, useEffect } from 'react';
import { 
  Mail, Lock, Eye, EyeOff, School, BookOpen, 
  AlertCircle, CheckCircle, Copy, Loader2, Shield, ArrowLeft 
} from 'lucide-react';
import { auth, db } from '../firebase';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

import backgroundImage from '../assets/intjangal.png';

const Connexion = ({ setIsAuthenticated }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [waveQrCodeUrl, setWaveQrCodeUrl] = useState(null);
  const [qrLoading, setQrLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  
  const [schoolData, setSchoolData] = useState(null);
  const [paymentStep, setPaymentStep] = useState('login'); 

  useEffect(() => {
    const fetchWaveQrCode = async () => {
      setQrLoading(true);
      try {
        const qrConfigRef = doc(db, 'system_config', 'wave_qr_code');
        const qrConfigSnap = await getDoc(qrConfigRef);
        if (qrConfigSnap.exists()) {
          setWaveQrCodeUrl(qrConfigSnap.data().qrCodeUrl);
        }
      } catch (error) {
        console.error("Erreur QR:", error);
      } finally {
        setQrLoading(false);
      }
    };
    fetchWaveQrCode();
  }, []);

  const checkSchoolSubscription = async (schoolId) => {
    if (!schoolId) return { valid: true }; // Pour les comptes sans école (ex: support)
    try {
      const schoolRef = doc(db, 'schools', schoolId);
      const schoolSnap = await getDoc(schoolRef);
      if (!schoolSnap.exists()) return { valid: false };
      
      const school = schoolSnap.data();
      if (school.subscriptionActive === false) return { valid: false, schoolData: school };
      
      const endDate = school.subscriptionEndDate?.toDate();
      if (endDate && endDate < new Date()) return { valid: false, schoolData: school };
      
      return { valid: true, schoolData: school };
    } catch (error) {
      return { valid: false };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });
    setAuthLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      const userDocSnap = await getDoc(doc(db, 'users', user.uid));

      if (!userDocSnap.exists()) {
        setMessage({ type: 'error', text: 'Compte non répertorié.' });
        await auth.signOut();
        return;
      }

      const data = userDocSnap.data();
      
      // Stockage des informations
      localStorage.setItem('userRole', data.role);
      if (data.schoolId) localStorage.setItem('userSchoolId', data.schoolId);

      // --- LOGIQUE DE REDIRECTION UNIFIÉE ---
      
      // 1. Si SuperAdmin : Accès immédiat
      if (data.role === 'superAdmin') {
        setIsAuthenticated(true);
        return;
      }

      // 2. Pour TOUS les autres (Admin, Enseignant, Étudiant, Parent)
      // On vérifie d'abord l'abonnement de leur école
      const subCheck = await checkSchoolSubscription(data.schoolId);
      
      if (subCheck.valid) {
        // Succès : On les envoie TOUS vers AdminHome (ou Home)
        setIsAuthenticated(true); 
      } else {
        // Échec : Blocage sur l'écran de paiement
        setSchoolData(subCheck.schoolData);
        setPaymentStep('payment');
      }

    } catch (error) {
      setMessage({ type: 'error', text: 'Email ou mot de passe incorrect.' });
    } finally {
      setAuthLoading(false);
    }
  };

  // ... (Reste du code renderPaymentScreen et renderLoginScreen identique au précédent)
  // Assurez-vous de garder les fonctions copyWaveNumber et handleForgotPassword
  
  const copyWaveNumber = () => {
    navigator.clipboard.writeText('773620212');
    setMessage({ type: 'success', text: 'Numéro Wave copié : 773620212' });
  };

  const handleForgotPassword = async () => {
    if (!email) { setMessage({ type: 'error', text: 'Entrez votre email.' }); return; }
    try {
      await sendPasswordResetEmail(auth, email);
      setMessage({ type: 'success', text: 'Email de récupération envoyé.' });
    } catch (error) { setMessage({ type: 'error', text: 'Erreur d\'envoi.' }); }
  };

  const renderPaymentScreen = () => (
    <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50 font-sans">
      <div className="max-w-4xl w-full bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row border border-slate-200">
        <div className="md:w-1/2 bg-gradient-to-br from-red-600 to-rose-800 p-10 text-white flex flex-col justify-between">
          <div>
            <button onClick={() => setPaymentStep('login')} className="flex items-center text-rose-100 hover:text-white mb-8 transition-colors">
              <ArrowLeft size={20} className="mr-2" /> Retour
            </button>
            <h2 className="text-3xl font-bold mb-4">Service Suspendu</h2>
            <p className="text-rose-100 opacity-90 leading-relaxed">
              L'accès pour l'école <span className="font-bold text-white">{schoolData?.name}</span> est actuellement restreint pour cause d'abonnement expiré.
            </p>
          </div>
          <div className="bg-white/10 p-6 rounded-2xl backdrop-blur-md border border-white/10 text-center">
            <div className="text-sm uppercase tracking-widest text-rose-100 mb-1 font-semibold">Réactivation</div>
            <div className="text-4xl font-black">10,000 FCFA</div>
          </div>
        </div>

        <div className="md:w-1/2 p-10 bg-white text-center flex flex-col justify-center">
          <h3 className="text-xl font-bold text-slate-800 mb-6">Paiement via Wave</h3>
          {qrLoading ? (
            <Loader2 className="animate-spin text-rose-600 mx-auto" size={40} />
          ) : (
            <div className="space-y-6">
              <div className="bg-white p-4 rounded-3xl border-2 border-rose-50 shadow-xl inline-block">
                <img src={waveQrCodeUrl || "https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=773620212"} alt="QR Code" className="w-64 h-64 object-contain" />
              </div>
              <button onClick={copyWaveNumber} className="w-full py-3 bg-slate-100 rounded-xl font-bold text-slate-600 flex items-center justify-center">
                <Copy size={18} className="mr-2" /> Copier le numéro
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderLoginScreen = () => (
    <div className="min-h-screen flex items-center justify-center p-4 bg-slate-100 font-sans relative">
      <div className="absolute inset-0 z-0 opacity-20 bg-cover bg-center" style={{ backgroundImage: `url(${backgroundImage})` }} />
      <div className="relative z-10 flex flex-col md:flex-row bg-white/90 backdrop-blur-xl rounded-[2.5rem] shadow-2xl w-full max-w-6xl overflow-hidden border border-white/20">
        <div className="md:w-5/12 bg-slate-900 text-white p-12 flex flex-col items-center justify-between text-center">
          <div>
            <div className="flex items-center justify-center space-x-3 mb-4">
              <School size={32} className="text-blue-400" />
              <h1 className="text-3xl font-black">JANGAL</h1>
            </div>
            <p className="text-slate-400 text-sm">Portail Multi-utilisateurs</p>
          </div>
          <div className="bg-white p-4 rounded-3xl">
             <img src={waveQrCodeUrl || "https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=773620212"} alt="QR" className="w-48 h-48" />
          </div>
          <p className="text-xs text-slate-500">Scannez pour régulariser l'abonnement</p>
        </div>

        <div className="md:w-7/12 p-10 md:p-16 bg-white">
          <h2 className="text-4xl font-black text-slate-800 mb-8">Connexion</h2>
          {message.text && (
            <div className={`p-4 mb-6 rounded-xl text-sm font-bold ${message.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
              {message.text}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-400 uppercase ml-1">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-5 py-4 bg-slate-100 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500" placeholder="votre@email.com" />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-400 uppercase ml-1">Mot de passe</label>
              <input type={showPassword ? 'text' : 'password'} required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-5 py-4 bg-slate-100 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500" placeholder="••••••••" />
            </div>
            <button type="submit" disabled={authLoading} className="w-full bg-blue-600 text-white py-5 rounded-2xl font-black hover:bg-blue-700 transition-all">
              {authLoading ? <Loader2 className="animate-spin mx-auto" /> : 'SE CONNECTER'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );

  return paymentStep === 'payment' ? renderPaymentScreen() : renderLoginScreen();
};

export default Connexion;